import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Skeleton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { AuthActionTypes, getUser, useAuth } from '@devd-client/devd/auth';
import {
  ConfigureColumns,
  ContributorSettingsIcon,
  DateFilter,
  DownloadButton,
  durationData,
  ISelect,
  Loader,
  MetricCol,
  MetricSidebarContainer,
  Paginate,
  PeriodType,
  TeamMenu,
  useDashboardStore,
  useToastHook,
} from '@devd-client/devd/components';
import moment from 'moment';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  downloadEngineeringStatsReport,
  useContributorTableMetrics,
} from '../../api/endpoints';
import { useEngineeringStatsGql } from '../../graphql';
import { EngineeringStatsItem } from '../../helpers/report.types';
import { EngineeringStatEmailGridCell } from './EmailCol';
import { EngineeringStatGridCell } from './EngineerStatsCol';

interface EngineerStatsField {
  field: keyof EngineeringStatsItem;
  name: string;
  numeric?: boolean;
}

const ENGINEER_STATS_FIELDS: EngineerStatsField[] = [
  { field: 'ticketCount', name: 'Ticket Count', numeric: true },
  { field: 'totalEffort', name: 'Total Effort', numeric: true },
  { field: 'devEffort', name: 'Dev Effort', numeric: true },
  { field: 'qAEffort', name: 'QA Effort', numeric: true },
  { field: 'products', name: 'Products' },
];

const PERMANENT_COLUMNS = [
  'Email',
  ...ENGINEER_STATS_FIELDS.map((f) => f.name),
];

const DEFAULT_PERMANENT_COLUMNS = [...PERMANENT_COLUMNS];

const STORAGE_KEY = 'engineerStatsColumns';
const PAGE_SIZE = 10;

interface MetricState {
  metricTitle: string;
  metricKey: string;
  sequence: number;
}

interface SavedPrefs {
  metrics: string[];
  permanent: string[];
}

const loadPrefs = (): SavedPrefs | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.metrics)) return null;
    return {
      metrics: parsed.metrics,
      permanent: Array.isArray(parsed.permanent) ? parsed.permanent : [],
    };
  } catch {
    return null;
  }
};

const noop = () => {
  // no-op for props we don't use
};

export const EngineerStatsTable = () => {
  const { selected, setSelected } = useDashboardStore();
  const [appState, dispatch] = useAuth();
  const [newToast] = useToastHook();
  const [downloadPending, setDownloadPending] = useState(false);
  const configureColumnsModal = useDisclosure();

  useEffect(() => {
    if (selected.startDate && selected.endDate) return;
    const sprintPref = appState?.user?.dto?.sprintDetail;
    if (appState?.user?.dto?.sprint && sprintPref) {
      try {
        const parsed = JSON.parse(sprintPref);
        setSelected({
          ...(durationData[6] as PeriodType),
          startDate: moment(parsed?.startDate).format('YYYY-MM-DD'),
          endDate: moment(parsed?.endDate).format('YYYY-MM-DD'),
          sprintId: appState.user.dto.sprint,
          sprintName: parsed?.sprintName,
        });
        return;
      } catch {
        // fall through
      }
    }
    setSelected(durationData[3] as PeriodType);
  }, []);

  const [selectedTeam, setSelectedTeam] = useState<ISelect>({
    value: appState?.user?.dto?.team || '',
    label: appState?.user?.dto?.team || 'Organisation',
  });
  const [currentPage, setCurrentPage] = useState(0);

  const { data: metricsData } = useContributorTableMetrics();
  const metricList = useMemo(() => metricsData?.dto || [], [metricsData]);

  const [selectedMetrics, setSelectedMetrics] = useState<MetricState[]>(() => {
    const prefs = loadPrefs();
    if (!prefs?.metrics?.length) return [];
    return prefs.metrics.map((metricKey, index) => ({
      metricKey,
      metricTitle: metricKey,
      sequence: index + 1,
    }));
  });

  useEffect(() => {
    if (!metricList.length) return;
    setSelectedMetrics((prev) =>
      prev.map((m) => {
        const matched = metricList.find(
          (item: any) =>
            item?.metric === m.metricKey || item?.id === m.metricKey
        );
        return {
          ...m,
          metricTitle: matched?.title ?? m.metricTitle,
        };
      })
    );
  }, [metricList]);

  const [selectedPermanentColumns, setSelectedPermanentColumns] = useState<
    string[]
  >(() => loadPrefs()?.permanent || DEFAULT_PERMANENT_COLUMNS);

  const teamIds = useMemo(
    () =>
      selectedTeam.value && selectedTeam.label !== 'Organisation'
        ? [selectedTeam.value]
        : [],
    [selectedTeam]
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [selected.startDate, selected.endDate, selected.sprintId, teamIds]);

  const { data, isFetching } = useEngineeringStatsGql(
    {
      startDate: selected.startDate,
      endDate: selected.endDate,
      teamIds,
      sprintId: selected.sprintId || '',
    },
    currentPage,
    PAGE_SIZE
  );

  const rows: EngineeringStatsItem[] = useMemo(
    () => data?.data ?? [],
    [data?.data]
  );
  const emails = useMemo(() => rows.map((r) => r.email), [rows]);

  const visibleEngineerStatsFields = useMemo(
    () =>
      ENGINEER_STATS_FIELDS.filter((f) =>
        selectedPermanentColumns.includes(f.name)
      ),
    [selectedPermanentColumns]
  );

  const rowHeight = '110px';

  const downloadSelectedColumns = useMemo(
    () => [
      ...selectedPermanentColumns,
      ...selectedMetrics.map((m) => m.metricKey),
    ],
    [selectedPermanentColumns, selectedMetrics]
  );

  const handleDownload = useCallback(async () => {
    if (!selected.startDate || !selected.endDate) return;

    setDownloadPending(true);
    try {
      const res = await downloadEngineeringStatsReport(
        {
          startDate: selected.startDate,
          endDate: selected.endDate,
          teamIds,
          sprintId: selected.sprintId || '',
        },
        downloadSelectedColumns
      );
      newToast({
        message:
          res.dto ||
          'Your request has been received. The engineering stats xlsx will be sent to your email shortly.',
        status: 'success',
      });
    } catch (err: unknown) {
      newToast({
        message:
          (err as { message?: string })?.message ||
          'There is some error in downloading data. Please try again or contact support at support@devdynamics.ai',
        status: 'error',
      });
    } finally {
      setDownloadPending(false);
    }
  }, [
    downloadSelectedColumns,
    newToast,
    selected.endDate,
    selected.sprintId,
    selected.startDate,
    teamIds,
  ]);

  const saveColumns = useCallback(
    (
      variables: { metrics: string[] },
      options?: {
        onSuccess?: (data: any) => void;
        onError?: (err: unknown) => void;
      }
    ) => {
      try {
        const all = variables.metrics ?? [];
        const permanent = all.filter((m) => PERMANENT_COLUMNS.includes(m));
        const metrics = all.filter((m) => !PERMANENT_COLUMNS.includes(m));
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ metrics, permanent })
        );
        options?.onSuccess?.({ status: 'success' });
      } catch (err) {
        newToast({
          message: 'Failed to save column preferences',
          status: 'error',
          duration: 3000,
        });
        options?.onError?.(err);
      }
    },
    [newToast]
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page - 1);
  }, []);

  const configureMetricList = useMemo(
    () =>
      (metricList || [])
        .filter(
          (metric: any) => !(metric?.metric === 'CUSTOM' && !metric?.metricType)
        )
        .map((metric: any) => {
          const isCustom =
            metric?.metric === 'CUSTOM' ||
            metric?.metricType === 'CUSTOM' ||
            metric?.source === 'CUSTOM';
          const key =
            metric?.metric === 'CUSTOM' ? metric?.title : metric?.metric;
          return {
            metricName: metric?.title ?? '',
            metricKey: key,
            tabs: isCustom ? ['Custom'] : metric?.tags,
            tooltip: metric?.info,
            id: metric?.id ?? key,
          };
        }),
    [metricList]
  );

  const HEADER_BG = '#FBFBFB';
  const BORDER = '1px solid #E0E0E0';

  const gridTemplateColumns = useMemo(() => {
    const parts = ['minmax(260px, 300px)'];
    visibleEngineerStatsFields.forEach((f) => {
      parts.push(
        f.field === 'products'
          ? 'minmax(280px, min(520px, 55vw))'
          : 'minmax(140px, 180px)'
      );
    });
    for (let i = 0; i < selectedMetrics.length; i++) {
      parts.push('minmax(200px, 260px)');
    }
    return parts.join(' ');
  }, [visibleEngineerStatsFields, selectedMetrics.length]);

  const showBodySkeleton = isFetching && rows.length === 0;
  const bodyRowCount = showBodySkeleton ? 10 : rows.length;

  return (
    <Flex direction="column" w="100%">
      <Flex align="center" justify="space-between">
        <Heading as="h1" size="md">
          Contributor Report
        </Heading>
        <Flex align="center" gap={3}>
          <DownloadButton downloadCSV={handleDownload} />
          <DateFilter
            getUser={getUser}
            appState={appState}
            dispatch={dispatch}
            AuthActionTypes={AuthActionTypes}
          />
        </Flex>
      </Flex>

      <Flex align="center" justify="space-between" mt={4}>
        <TeamMenu
          selectedTeam={selectedTeam}
          handleSelectedTeam={setSelectedTeam}
          AuthActionTypes={AuthActionTypes}
          getUser={getUser}
          appState={appState}
          dispatch={dispatch}
          hideShowDetails
        />

        <Button
          bg="transparent"
          border="1px solid #E0E0E0"
          borderRadius="4px"
          color="text.secondary2"
          size="sm"
          fontWeight="normal"
          onClick={configureColumnsModal.onOpen}
          leftIcon={<ContributorSettingsIcon />}
        >
          Configure Columns
        </Button>
      </Flex>

      <Box
        mt={3}
        overflowX="auto"
        overflowY="auto"
        border={BORDER}
        height="66vh"
        width="100%"
        borderRadius="8px"
        position="relative"
        bg="white"
      >
        <Grid templateColumns={gridTemplateColumns} w="max-content" minW="100%">
          <GridItem
            position="sticky"
            top={0}
            left={0}
            zIndex={21}
            bg={HEADER_BG}
            minH="52px"
            display="flex"
            alignItems="center"
            px={4}
            py={2}
            borderBottom={BORDER}
            boxShadow="1px 0 0 0 #E0E0E0"
          >
            <Text
              fontSize="sm"
              fontFamily="heading"
              fontWeight="medium"
              color="text.secondary2"
            >
              Email
            </Text>
          </GridItem>

          {visibleEngineerStatsFields.map((f, fi) => (
            <GridItem
              key={`h-${f.field}`}
              position="sticky"
              top={0}
              zIndex={20}
              bg={HEADER_BG}
              minH="52px"
              display="flex"
              alignItems="center"
              px={4}
              borderLeft={BORDER}
              borderBottom={BORDER}
              borderRight={
                fi === visibleEngineerStatsFields.length - 1 &&
                selectedMetrics.length === 0
                  ? BORDER
                  : undefined
              }
            >
              <Tooltip label={f.name} hasArrow isDisabled={f.name.length < 22}>
                <Text
                  fontSize="sm"
                  fontFamily="heading"
                  fontWeight="medium"
                  color="text.secondary2"
                  noOfLines={1}
                >
                  {f.name}
                </Text>
              </Tooltip>
            </GridItem>
          ))}

          {selectedMetrics.map((metric, mi) => {
            const mt = metricList?.find(
              (item: any) =>
                item?.metric === metric.metricKey ||
                item?.id === metric.metricKey
            );
            const headerTitle =
              metric.metricTitle === 'Acceptance Rate By Language'
                ? 'Acceptance Rate'
                : metric.metricTitle;
            return (
              <GridItem
                key={`mh-${metric.metricKey}`}
                gridRow={1}
                gridColumn={2 + visibleEngineerStatsFields.length + mi}
                position="sticky"
                top={0}
                zIndex={20}
                bg={HEADER_BG}
                minH="52px"
                display="flex"
                alignItems="center"
                px={4}
                borderLeft={BORDER}
                borderBottom={BORDER}
                borderRight={
                  mi === selectedMetrics.length - 1 ? BORDER : undefined
                }
              >
                <Tooltip label={mt?.info || headerTitle} hasArrow>
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    fontWeight="medium"
                    color="text.secondary2"
                    noOfLines={1}
                    wordBreak="break-all"
                  >
                    {headerTitle}
                  </Text>
                </Tooltip>
              </GridItem>
            );
          })}

          {showBodySkeleton
            ? [...Array(bodyRowCount)].map((_, ri) => (
                <Fragment key={`sk-row-${ri}`}>
                  <GridItem
                    position="sticky"
                    left={0}
                    zIndex={10}
                    bg="white"
                    minH={rowHeight}
                    display="flex"
                    alignItems="flex-start"
                    py={3}
                    px={3}
                    pl={4}
                    borderBottom={BORDER}
                    boxShadow="1px 0 0 0 #E0E0E0"
                    gridRow={ri + 2}
                    gridColumn={1}
                  >
                    <Flex align="flex-start" gap={2} w="full">
                      <Skeleton
                        height="24px"
                        width="24px"
                        borderRadius="full"
                      />
                      <Skeleton height="14px" width="180px" />
                    </Flex>
                  </GridItem>
                  {visibleEngineerStatsFields.map((f, fi) => (
                    <GridItem
                      key={`sk-${ri}-${f.field}`}
                      minH={rowHeight}
                      display="flex"
                      alignItems="flex-start"
                      px={4}
                      py={3}
                      borderLeft={BORDER}
                      borderBottom={BORDER}
                      borderRight={
                        fi === visibleEngineerStatsFields.length - 1 &&
                        selectedMetrics.length === 0
                          ? BORDER
                          : undefined
                      }
                      bg="white"
                      gridRow={ri + 2}
                      gridColumn={fi + 2}
                    >
                      {f.field === 'products' ? (
                        <Flex wrap="wrap" gap={1.5} w="full">
                          {[1, 2, 3].map((k) => (
                            <Skeleton
                              key={k}
                              height="22px"
                              width="100px"
                              borderRadius="md"
                            />
                          ))}
                        </Flex>
                      ) : (
                        <Skeleton height="14px" width="60px" />
                      )}
                    </GridItem>
                  ))}
                  {selectedMetrics.map((m, mi) => (
                    <GridItem
                      key={`sk-${ri}-${m.metricKey}`}
                      minH={rowHeight}
                      display="flex"
                      alignItems="flex-start"
                      borderLeft={BORDER}
                      borderBottom={BORDER}
                      borderRight={
                        mi === selectedMetrics.length - 1 ? BORDER : undefined
                      }
                      bg="white"
                      p={3}
                      gridRow={ri + 2}
                      gridColumn={2 + visibleEngineerStatsFields.length + mi}
                    >
                      <Flex direction="column" gap={2} w="full">
                        <Flex gap={2}>
                          <Skeleton h="8px" w="8px" borderRadius="full" />
                          <Skeleton h="20px" w="60px" borderRadius="md" />
                        </Flex>
                        <Skeleton h="24px" w="100%" borderRadius="md" />
                      </Flex>
                    </GridItem>
                  ))}
                </Fragment>
              ))
            : rows.map((row, ri) => (
                <Fragment key={row.email}>
                  <GridItem
                    position="sticky"
                    left={0}
                    zIndex={10}
                    bg="white"
                    minH={rowHeight}
                    display="flex"
                    alignItems="flex-start"
                    py={3}
                    px={3}
                    pl={4}
                    borderBottom={BORDER}
                    boxShadow="1px 0 0 0 #E0E0E0"
                    gridRow={ri + 2}
                    gridColumn={1}
                  >
                    <EngineeringStatEmailGridCell email={row.email} />
                  </GridItem>
                  {visibleEngineerStatsFields.map((f, fi) => (
                    <GridItem
                      key={`${row.email}-${f.field}`}
                      minH={rowHeight}
                      display="flex"
                      alignItems="flex-start"
                      px={4}
                      py={3}
                      borderLeft={BORDER}
                      borderBottom={BORDER}
                      borderRight={
                        fi === visibleEngineerStatsFields.length - 1 &&
                        selectedMetrics.length === 0
                          ? BORDER
                          : undefined
                      }
                      bg="white"
                      gridRow={ri + 2}
                      gridColumn={fi + 2}
                    >
                      <EngineeringStatGridCell
                        row={row}
                        field={f.field}
                        numeric={f.numeric}
                      />
                    </GridItem>
                  ))}
                </Fragment>
              ))}

          {!showBodySkeleton &&
            selectedMetrics.map((metric, mi) => (
              <MetricCol
                key={metric.metricKey}
                gridLayout={{
                  columnStart: 2 + visibleEngineerStatsFields.length + mi,
                  rowContributors: emails,
                }}
                metricTooltip={
                  metricList?.find(
                    (item: any) =>
                      item?.metric === metric.metricKey ||
                      item?.id === metric.metricKey
                  ) || undefined
                }
                metricList={metricList}
                selectedMetrics={selectedMetrics}
                contributors={emails}
                title={metric.metricTitle}
                metricKey={metric.metricKey}
                currentPage={0}
                setCurrentPage={noop}
                setTotalPages={noop}
                scrollToTop={noop}
                loading={isFetching}
                initialOrderBy=""
                setOrderByUrl={noop}
                setOrderColumn={noop}
                orderColumn=""
                compareTo=""
                searchTerm=""
                jobRoleState={[]}
                repoState={[]}
                languageState={[]}
                activityFilter={null}
                teamState={teamIds}
                metricFilterState={[]}
                rowHeight={rowHeight}
                startDate={selected.startDate}
                endDate={selected.endDate}
                showContributorPercentiles={false}
                disableSort
                sidebarTeamId={selectedTeam.value}
              />
            ))}
        </Grid>
      </Box>

      <Box marginLeft="-20px">
        <Paginate
          currentPage={(data?.currentPage ?? 0) + 1}
          totalPageCount={data?.totalPages ?? 1}
          pageSize={PAGE_SIZE}
          onPageChange={(page) => handlePageChange(page as number)}
        />
      </Box>

      <MetricSidebarContainer />

      {configureColumnsModal.isOpen && (
        <ConfigureColumns
          {...configureColumnsModal}
          componentOrigin="engineer-stats"
          saveColumns={saveColumns as any}
          saveColumnsIsLoading={false}
          metricList={configureMetricList}
          selectedMetrics={selectedMetrics}
          setSelectedMetrics={setSelectedMetrics}
          selectedPermanentColumns={selectedPermanentColumns}
          setSelectedPermanentColumns={setSelectedPermanentColumns}
          permanentColumns={PERMANENT_COLUMNS}
          defaultMetrics={[]}
        />
      )}

      {downloadPending && <Loader />}
    </Flex>
  );
};

export default EngineerStatsTable;
